<script setup lang="ts">
	import { fetchAuthSession, signOut } from "@aws-amplify/auth";
	import CompassIcon from "@/icons/CompassIcon.vue";
	import SettingsIcon from "@/icons/SettingsIcon.vue";
	import { useMap } from "@ptx-autonomous/libs/maplibre";
	import { onMounted, onUnmounted, ref, computed } from "vue";
	import { useRouter } from "vue-router";
	import type { CognitoIdTokenPayload } from "@shared/types/auth";
	import { useQueryClient } from "@tanstack/vue-query";

	const { map } = useMap();
	const user = ref<CognitoIdTokenPayload | null>(null);

	const isFlightPathAdmin = computed(() => {
		const groups = user.value?.["cognito:groups"] as string[] | undefined;
		return groups?.includes("FlightPathAdmin") ?? false;
	});

	const checkAuthStatus = async () => {
		try {
			const session = await fetchAuthSession();
			if (session.tokens?.idToken) {
				user.value = session.tokens.idToken
					.payload as CognitoIdTokenPayload;
			}
		} catch (error) {
			user.value = null;
		}
	};

	onMounted(() => {
		checkAuthStatus();
	});

	const mapBearing = ref(0);

	function resetMapCompass() {
		map.easeTo({
			bearing: 0,
			pitch: 0,
			duration: 200,
		});
	}

	const updateBearing = () => {
		mapBearing.value = map.getBearing();
	};

	onMounted(() => {
		mapBearing.value = map.getBearing();
		map.on("rotate", updateBearing);
		map.on("rotateend", updateBearing);
	});

	onUnmounted(() => {
		map.off("rotate", updateBearing);
		map.off("rotateend", updateBearing);
	});

	const emits = defineEmits(["open-settings"]);
	const router = useRouter();
	const isExpanded = ref(false);
	const queryClient = useQueryClient();

	function toggleExpanded() {
		isExpanded.value = !isExpanded.value;
	}

	async function handleLogout() {
		try {
			await signOut();
			user.value = null;
			queryClient.clear();
		} catch (error) {
			console.error("Logout failed:", error);
		}
		router.push({ name: "auth" });
	}
</script>

<template>
	<div
		class="absolute top-6 right-6 bg-white shadow-lg rounded-lg p-2 z-10 transition-all duration-300"
	>
		<transition
			enter-active-class="transition-all duration-100 ease-out"
			leave-active-class="transition-all duration-100 ease-in"
			enter-from-class="opacity-0 transform scale-95"
			enter-to-class="opacity-100 transform scale-100"
			leave-from-class="opacity-100 transform scale-100"
			leave-to-class="opacity-0 transform scale-95"
			mode="out-in"
		>
			<!-- Collapsed State -->
			<div
				v-if="!isExpanded"
				@click="toggleExpanded"
				class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-grey-50 rounded-lg transition-colors"
				title="Expand menu"
				key="collapsed"
			>
				<div class="z-10">
					<img
						src="/ptx-logo.svg"
						alt="PTX Logo"
						class="h-10"
					/>
				</div>
				<div
					class="text-grey-400 hover:text-grey-600 p-1 transition-colors"
				>
					<svg
						class="w-5 h-5"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M19 9l-7 7-7-7"
						></path>
					</svg>
				</div>
			</div>

			<!-- Expanded State -->
			<div
				v-else
				class="p-1"
				key="expanded"
			>
				<div
					@click="toggleExpanded"
					class="flex items-center gap-2 mb-2 cursor-pointer hover:bg-grey-50 rounded-lg p-2 transition-colors"
					title="Collapse menu"
				>
					<div class="z-10">
						<img
							src="/ptx-logo.svg"
							alt="PTX Logo"
							class="h-10"
						/>
					</div>
					<div class="text-sm flex-1">
						<div class="font-medium text-grey-950">
							{{ user?.email || "User" }}
						</div>
						<div class="text-grey-500">
							{{ isFlightPathAdmin ? "Admin" : "User" }}
						</div>
					</div>
					<div
						class="text-grey-400 hover:text-grey-600 p-1 transition-colors"
					>
						<svg
							class="w-5 h-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M5 15l7-7 7 7"
							></path>
						</svg>
					</div>
				</div>

				<div
					class="flex items-center justify-center gap-2 bg-grey-100 rounded-lg p-2"
				>
					<button
						@click="resetMapCompass"
						class="text-grey-600 hover:text-grey-800 p-2 transition-transform duration-300"
						title="Reset map orientation (N: {{ Math.round(mapBearing) }}°)"
						:style="{ transform: `rotate(${mapBearing - 45}deg)` }"
					>
						<CompassIcon class="w-6 h-6" />
					</button>
					<button
						@click="emits('open-settings')"
						class="text-grey-600 hover:text-grey-800 p-2"
						title="Settings"
					>
						<SettingsIcon class="w-6 h-6" />
					</button>
					<button
						@click="handleLogout"
						class="text-grey-600 hover:text-grey-800 p-2"
						title="Sign out"
					>
						<svg
							class="w-6 h-6"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
							></path>
						</svg>
					</button>
				</div>
			</div>
		</transition>
	</div>
</template>
